/**
 * User Management Module
 *
 * @module user
 * @category User
 *
 * Handles user profile and account management:
 * - Get current user info
 * - Update profile
 * - Change password
 */

import type { HttpClient, ClientResult } from "@amaster.ai/http-client";
import type { User, UpdateMeParams, ChangePasswordParams, SuccessResponse } from "../types";

export interface UserModuleDeps {
  http: HttpClient;
  storage: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  };
  onUserUpdate: (user: User) => void;
  onAuthFailure?: (result: ClientResult<User>) => void;
}

export function createUserModule(deps: UserModuleDeps) {
  const { http, storage, onUserUpdate, onAuthFailure } = deps;

  return {
    /**
     * Get current logged-in user information
     *
     * @category User
     * @example
     * ```typescript
     * const result = await user.getMe();
     * if (result.data) {
     *   console.log("User:", result.data.displayName);
     * }
     * ```
     */
    async getMe(): Promise<ClientResult<User>> {
      const token = storage.getItem("amaster_access_token");
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      const result = await http.request<User>({
        url: "/api/auth/me",
        method: "get",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (result.data) {
        onUserUpdate(result.data);
      } else if (result.status === 401 || result.status === 403) {
        onAuthFailure?.(result);
      }

      return result;
    },

    /**
     * Update current user's profile
     *
     * @category User
     * @example
     * ```typescript
     * await user.updateMe({
     *   displayName: "New Name",
     *   avatarUrl: "https://example.com/avatar.jpg",
     * });
     * ```
     */
    async updateMe(params: UpdateMeParams): Promise<ClientResult<User>> {
      const token = storage.getItem("amaster_access_token");
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      const result = await http.request<User>({
        url: "/api/auth/me",
        method: "put",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: params,
      });

      if (result.data) {
        onUserUpdate(result.data);
      }

      return result;
    },

    /**
     * Change current user's password
     *
     * @category User
     * @example
     * ```typescript
     * await user.changePassword({
     *   oldPassword: "OldPass@123",
     *   newPassword: "NewPass@456",
     * });
     * ```
     */
    async changePassword(params: ChangePasswordParams): Promise<ClientResult<SuccessResponse>> {
      const token = storage.getItem("amaster_access_token");
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      return http.request<SuccessResponse>({
        url: "/api/auth/change-password",
        method: "post",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: params,
      });
    },
  };
}

export type UserModule = ReturnType<typeof createUserModule>;
